import React, { useEffect, useState } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import Modal from './Modal';
import { Bar, Line } from 'react-chartjs-2';
import MoonLoader from 'react-spinners/MoonLoader';
import { formatCurrency } from '../Help/helper';
import moment from 'moment';
import BreadCrumb from '../BreadCrumb';
import { useAuth } from '../Auth/Auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  UserGroupIcon,
  TruckIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const pages = [
  { name: 'Reports', to: '/analytics', component: Link, current: false },
  { name: 'Customer Analysis', to: '/analytics/customers', component: Link, current: true },
];

const CustomerJobHistory = () => {
  const [customers, setCustomers] = useState([]);
  const { role, loading } = useAuth();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [totalSales, setTotalSales] = useState(0);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [jobCount, setJobCount] = useState(0);
  const [avgJobValue, setAvgJobValue] = useState(0);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCustomersByValue, setTopCustomersByValue] = useState([]);
  const [customerTrend, setCustomerTrend] = useState([]);

  // Load initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        await Promise.all([
          fetchCustomers(),
          fetchTopCustomersData(),
          fetchCustomerTrends()
        ]);
      } catch (error) {
        logger.error('Error loading initial data:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setInitialLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const fetchCustomers = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'customers'));
      const customersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersList);
    } catch (error) {
      logger.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    }
  };

  // Optimized: Fetch top customers data with better performance
  const fetchTopCustomersData = async () => {
    try {
      // Build lookup maps for better performance
      const vehiclesSnapshot = await getDocs(collection(db, 'vehicles'));
      const vehicleCustomerMap = {};
      vehiclesSnapshot.forEach(doc => {
        vehicleCustomerMap[doc.id] = doc.data().customerId;
      });

      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const customerJobCounts = {};

      jobsSnapshot.forEach(doc => {
        const vehicleId = doc.data().vehicleId;
        const customerId = vehicleCustomerMap[vehicleId];
        if (customerId) {
          customerJobCounts[customerId] = (customerJobCounts[customerId] || 0) + 1;
        }
      });

      // Fetch invoice values - Optimized to avoid N+1
      const invoicesSnapshot = await getDocs(collection(db, 'newInvoices'));
      const customerInvoiceValues = {};

      // Build job lookup map by jobCardNo to avoid repeated queries
      const jobsByJobCardNo = {};
      jobsSnapshot.forEach(doc => {
        const jobCardNo = doc.data().jobCardNo;
        if (jobCardNo) {
          jobsByJobCardNo[jobCardNo] = doc.data();
        }
      });

      // Process invoices using the lookup map
      invoicesSnapshot.forEach(invoiceDoc => {
        const jobCardNo = invoiceDoc.data().jobCardNo;
        const job = jobsByJobCardNo[jobCardNo];

        if (job) {
          const vehicleId = job.vehicleId;
          const customerId = vehicleCustomerMap[vehicleId];
          if (customerId) {
            const value = Number(invoiceDoc.data().totalSale) || 0;
            customerInvoiceValues[customerId] = (customerInvoiceValues[customerId] || 0) + value;
          }
        }
      });

      // Get customer details using batch fetch - Optimized to avoid N+1
      const customerIds = Object.keys(customerJobCounts);
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      const customersMap = {};
      customersSnapshot.forEach(doc => {
        customersMap[doc.id] = doc.data();
      });

      const validCustomers = customerIds.map(customerId => {
        const data = customersMap[customerId];
        if (data) {
          return {
            id: customerId,
            name: `${data.firstName} ${data.lastName}`,
            jobCount: customerJobCounts[customerId] || 0,
            totalValue: customerInvoiceValues[customerId] || 0
          };
        }
        return null;
      }).filter(c => c !== null);

      const topByJobs = [...validCustomers].sort((a, b) => b.jobCount - a.jobCount).slice(0, 10);
      const topByValue = [...validCustomers].sort((a, b) => b.totalValue - a.totalValue).slice(0, 10);

      setTopCustomers(topByJobs);
      setTopCustomersByValue(topByValue);
    } catch (error) {
      logger.error('Error fetching top customers:', error);
      toast.error('Failed to load top customers data');
    }
  };

  // Fetch customer activity trends (last 6 months)
  const fetchCustomerTrends = async () => {
    try {
      const sixMonthsAgo = moment().subtract(6, 'months').toDate();
      const jobsQuery = query(collection(db, 'jobs'), where('createdAt', '>=', sixMonthsAgo));
      const jobsSnapshot = await getDocs(jobsQuery);

      const monthlyData = {};
      jobsSnapshot.forEach(doc => {
        const createdAt = doc.data().createdAt?.toDate();
        if (createdAt) {
          const monthKey = moment(createdAt).format('MMM YYYY');
          monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
        }
      });

      const last6Months = [];
      for (let i = 5; i >= 0; i--) {
        const month = moment().subtract(i, 'months').format('MMM YYYY');
        last6Months.push({
          month,
          count: monthlyData[month] || 0
        });
      }

      setCustomerTrend(last6Months);
    } catch (error) {
      logger.error('Error fetching trends:', error);
    }
  };

  // Fetch customer-specific data
  useEffect(() => {
    if (selectedCustomer) {
      setIsLoading(true);
      const fetchCustomerData = async () => {
        try {
          const vehiclesQuery = query(collection(db, 'vehicles'), where('customerId', '==', selectedCustomer.id));
          const vehiclesSnapshot = await getDocs(vehiclesQuery);
          const vehicleIds = vehiclesSnapshot.docs.map(doc => doc.id);
          setVehicleCount(vehiclesSnapshot.size);

          let allJobs = [];
          let total = 0;

          for (const vehicleId of vehicleIds) {
            const jobsQuery = query(collection(db, 'jobs'), where('vehicleId', '==', vehicleId));
            const jobsSnapshot = await getDocs(jobsQuery);
            const jobsData = jobsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              vehicleId,
              createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate().toLocaleDateString() : 'N/A',
            }));

            allJobs = allJobs.concat(jobsData);

            // Fetch invoices for each job
            for (const job of jobsData) {
              const invoicesQuery = query(collection(db, 'newInvoices'), where('jobCardNo', '==', job.jobCardNo));
              const invoicesSnapshot = await getDocs(invoicesQuery);
              for (const invoiceDoc of invoicesSnapshot.docs) {
                total += Number(invoiceDoc.data().totalSale || 0);
              }
            }
          }

          setJobs(allJobs);
          setJobCount(allJobs.length);
          setTotalSales(total);
          setAvgJobValue(allJobs.length > 0 ? total / allJobs.length : 0);
        } catch (error) {
          logger.error('Error fetching customer data:', error);
          toast.error('Failed to load customer data');
        } finally {
          setIsLoading(false);
        }
      };

      fetchCustomerData();
    } else {
      setJobs([]);
      setTotalSales(0);
      setVehicleCount(0);
      setJobCount(0);
      setAvgJobValue(0);
    }
  }, [selectedCustomer]);

  const handleCustomerChange = (event) => {
    const customer = customers.find(c => c.id === event.target.value);
    setSelectedCustomer(customer);
  };

  const openModal = (job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  // Chart configurations with modern styling
  const topCustomersChartData = {
    labels: topCustomers.map(c => c.name),
    datasets: [{
      label: 'Number of Job Cards',
      data: topCustomers.map(c => c.jobCount),
      backgroundColor: 'rgba(99, 102, 241, 0.7)',
      borderColor: 'rgba(99, 102, 241, 1)',
      borderWidth: 2,
    }]
  };

  const topValueChartData = {
    labels: topCustomersByValue.map(c => c.name),
    datasets: [{
      label: 'Total Revenue',
      data: topCustomersByValue.map(c => c.totalValue),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderColor: 'rgba(34, 197, 94, 1)',
      borderWidth: 2,
    }]
  };

  const trendChartData = {
    labels: customerTrend.map(d => d.month),
    datasets: [{
      label: 'Job Cards per Month',
      data: customerTrend.map(d => d.count),
      fill: true,
      backgroundColor: 'rgba(168, 85, 247, 0.2)',
      borderColor: 'rgba(168, 85, 247, 1)',
      borderWidth: 3,
      tension: 0.4,
      pointBackgroundColor: 'rgba(168, 85, 247, 1)',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 5,
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return label.includes('Revenue') ? `${label}: ${formatCurrency(value)}` : `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          callback: function(value) {
            return this.getLabelForValue(value).toString().includes('Revenue') ? formatCurrency(value) : value;
          }
        }
      },
      x: {
        grid: {
          display: false,
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (role !== 'Finance' && role !== 'Admin' && role !== 'Service Advisor') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading customer analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 py-4">
      <BreadCrumb pages={pages} />
      <ToastContainer />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Customer Analytics</h1>
              <p className="text-sm text-gray-600 mt-1">Comprehensive customer insights and trends</p>
            </div>
          </div>
        </div>

        {/* Overview Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Customers by Job Count */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <ChartBarIcon className="w-6 h-6" />
                Top 10 Customers by Job Volume
              </h3>
              <p className="text-indigo-100 text-xs mt-1">Most active customers by number of job cards</p>
            </div>
            <div className="p-6" style={{ height: '400px' }}>
              {topCustomers.length > 0 ? (
                <Bar data={topCustomersChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </div>

          {/* Top Customers by Revenue */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <CurrencyDollarIcon className="w-6 h-6" />
                Top 10 Customers by Revenue
              </h3>
              <p className="text-emerald-100 text-xs mt-1">Highest value customers by total invoice amount</p>
            </div>
            <div className="p-6" style={{ height: '400px' }}>
              {topCustomersByValue.length > 0 ? (
                <Bar data={topValueChartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Activity Trend */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <ArrowTrendingUpIcon className="w-6 h-6" />
              Customer Activity Trend (Last 6 Months)
            </h3>
            <p className="text-purple-100 text-xs mt-1">Monthly job card volume trend</p>
          </div>
          <div className="p-6" style={{ height: '350px' }}>
            {customerTrend.length > 0 ? (
              <Line data={trendChartData} options={chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* Customer Details Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
            <h3 className="text-xl font-bold text-white">Customer Details & Job History</h3>
            <p className="text-blue-100 text-xs mt-1">Select a customer to view detailed history</p>
          </div>

          <div className="p-6">
            {/* Customer Selector */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Customer
              </label>
              <select
                onChange={handleCustomerChange}
                value={selectedCustomer?.id || ''}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choose a customer...</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.firstName} {customer.lastName}
                  </option>
                ))}
              </select>
            </div>

            {selectedCustomer && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-4 border-blue-500 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-blue-600 uppercase">Vehicles</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{vehicleCount}</p>
                      </div>
                      <TruckIcon className="w-10 h-10 text-blue-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-l-4 border-indigo-500 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-indigo-600 uppercase">Total Jobs</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{jobCount}</p>
                      </div>
                      <BriefcaseIcon className="w-10 h-10 text-indigo-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-emerald-600 uppercase">Total Revenue</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalSales)}</p>
                      </div>
                      <CurrencyDollarIcon className="w-10 h-10 text-emerald-500 opacity-50" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-l-4 border-purple-500 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-purple-600 uppercase">Avg Job Value</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(avgJobValue)}</p>
                      </div>
                      <ChartBarIcon className="w-10 h-10 text-purple-500 opacity-50" />
                    </div>
                  </div>
                </div>

                {/* Customer Info Card */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 mb-6 border border-gray-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Customer Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{selectedCustomer.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Phone</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{selectedCustomer.phoneNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Registered Since</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {selectedCustomer.dateOfBirth ? moment(selectedCustomer.dateOfBirth).format('DD-MMM-YYYY') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Jobs Table */}
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="text-center">
                      <MoonLoader size={50} color="#6366F1" />
                      <p className="mt-4 text-sm text-gray-600">Loading job history...</p>
                    </div>
                  </div>
                ) : jobs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-black text-white uppercase tracking-wider">
                            Job Card No
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-white uppercase tracking-wider">
                            Vehicle
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-white uppercase tracking-wider">
                            Reg No
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-white uppercase tracking-wider">
                            Date Created
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-black text-white uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {jobs.map((job) => (
                          <tr key={job.id} className="hover:bg-blue-50 transition-colors duration-150">
                            <td className="px-4 py-3 text-xs font-medium text-gray-900">
                              {job.jobCardNo}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700">
                              {job.vehicleMake} {job.vehicleModel}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700">
                              {job.registrationNumber || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700">
                              {job.createdAt}
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <button
                                onClick={() => openModal(job)}
                                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors duration-200"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BriefcaseIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No job cards found for this customer</p>
                  </div>
                )}
              </>
            )}

            {!selectedCustomer && (
              <div className="text-center py-12">
                <UserGroupIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a customer to view detailed job history</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && <Modal job={selectedJob} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default CustomerJobHistory;
